'use client'

/**
 * On-device diagnostic panel — visible live state for iOS testers who can't
 * open Safari Web Inspector. Gated by VITE_ENABLE_DEBUG_TOOLS=1 in
 * prod, identical to DebugNukeButton. Mounts bottom-right so it doesn't
 * collide with the nuke button.
 *
 * Shows:
 *   - PowerSync status (connected / hasSynced / last error / flow direction)
 *   - Connectivity (navigator.onLine + ConnectivityService verdict)
 *   - Intent queue counts by status/type + last errors + oldest age
 *   - Offline blob queue depth
 *   - User / build info
 *
 * Actions: ping /api/health (measures RTT), ping /api/version, copy full
 * dump as plaintext for the tester to paste into chat.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePathname } from '@/hooks/usePathname'
import { useStatus } from '@powersync/react'
import { useAuth } from '@/components/AuthProvider'
import { ConnectivityService } from '@/lib/connectivity'
import { apiFetch } from '@/lib/api'
import { allIntents } from '@/lib/intent-queue'
import type { IntentRecord } from '@/lib/db'
import { getOfflineQueue, purgeAllIntents } from '@/lib/db'
import { reconcilePendingAnalyses } from '@/lib/intent-worker'
import { isMarketingPath } from '@/lib/route-gates'
import { readErrors, clearErrors, readHistoryCalls, clearHistoryCalls, type ErrorEntry, type HistoryCallEntry } from '@/lib/error-buffer'
import { readPsLifecycle, clearPsLifecycle, type PsLifecycleEntry } from '@/lib/powersync/lifecycle-log'
import { readConnLog, type ConnLogEntry } from '@/lib/connectivity-log'
import { getPowerSync } from '@/lib/powersync/client'
import buildInfo from '@/build-info.json'
import { toast } from 'sonner'

interface DeadLetterRow {
  id: string
  op_table: string
  op_type: string
  row_id: string
  error_code: string | null
  discarded_at: string | null
}

interface PingResult {
  url: string
  ok: boolean
  status: number | null
  latencyMs: number
  error?: string
  at: number
}

function humanAge(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rs = s % 60
  if (m < 60) return `${m}m${rs}s`
  const h = Math.floor(m / 60)
  return `${h}h${m % 60}m`
}

function summariseIntents(intents: IntentRecord[]) {
  const byStatus: Record<string, number> = {}
  const byType: Record<string, number> = {}
  const errorSamples: Array<{ type: string; error: string; ageMs: number; attempts: number }> = []
  let oldestAgeMs = 0
  const now = Date.now()
  for (const i of intents) {
    byStatus[i.status] = (byStatus[i.status] ?? 0) + 1
    byType[i.type] = (byType[i.type] ?? 0) + 1
    if (i.status !== 'done') {
      const age = now - i.createdAt
      if (age > oldestAgeMs) oldestAgeMs = age
    }
    if (i.lastError && errorSamples.length < 5) {
      errorSamples.push({
        type: i.type,
        error: i.lastError,
        ageMs: now - i.createdAt,
        attempts: i.attempts,
      })
    }
  }
  return { total: intents.length, byStatus, byType, errorSamples, oldestAgeMs }
}

/**
 * Outer gate: useStatus() crashes with "Cannot read properties of null" when
 * no PowerSyncContext.Provider is above it (login, marketing, unauth tree).
 * Bail out before mounting the inner component that actually calls the hook.
 */
export function DebugDiagnostics() {
  const { user, userRole, loading } = useAuth()
  const pathname = usePathname()
  if (loading || !user) return null
  if (isMarketingPath(pathname)) return null
  if (process.env.NODE_ENV === 'production' && process.env.VITE_ENABLE_DEBUG_TOOLS !== '1') return null
  return <DebugDiagnosticsInner user={user} userRole={userRole} />
}

interface InnerProps {
  user: { id: string }
  userRole: string | null | undefined
}

function DebugDiagnosticsInner({ user, userRole }: InnerProps) {
  const status = useStatus()
  const [open, setOpen] = useState(false)
  const [intents, setIntents] = useState<IntentRecord[]>([])
  const [offlineQueueDepth, setOfflineQueueDepth] = useState<number | null>(null)
  const [navOnline, setNavOnline] = useState<boolean>(true)
  const [svcOnline, setSvcOnline] = useState<boolean>(true)
  const [pings, setPings] = useState<PingResult[]>([])
  const [copied, setCopied] = useState(false)
  const [refreshTick, setRefreshTick] = useState(0)
  const [recentErrors, setRecentErrors] = useState<ErrorEntry[]>([])
  const [historyCalls, setHistoryCalls] = useState<HistoryCallEntry[]>([])
  const [lifecycle, setLifecycle] = useState<PsLifecycleEntry[]>([])
  const [connLog, setConnLog] = useState<ConnLogEntry[]>([])
  const [deadLetters, setDeadLetters] = useState<DeadLetterRow[]>([])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const updateNav = () => setNavOnline(navigator.onLine)
    updateNav()
    window.addEventListener('online', updateNav)
    window.addEventListener('offline', updateNav)
    const unsub = ConnectivityService.getInstance().subscribe((o) => setSvcOnline(o))
    return () => {
      window.removeEventListener('online', updateNav)
      window.removeEventListener('offline', updateNav)
      unsub()
    }
  }, [])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const load = async () => {
      const [all, q] = await Promise.all([
        allIntents().catch(() => null),
        getOfflineQueue().catch(() => null),
      ])
      if (cancelled) return
      if (all) setIntents(all)
      if (q) setOfflineQueueDepth(q.length)
      // readErrors() always returns a fresh array — guard against no-op
      // updates so the dump useMemo doesn't rebuild every 3s tick.
      const next = readErrors()
      setRecentErrors((prev) => (
        prev.length === next.length && prev[prev.length - 1]?.ts === next[next.length - 1]?.ts
          ? prev
          : next
      ))
      const nextHist = readHistoryCalls()
      setHistoryCalls((prev) => (
        prev.length === nextHist.length && prev[prev.length - 1]?.ts === nextHist[nextHist.length - 1]?.ts
          ? prev
          : nextHist
      ))
      const nextLc = readPsLifecycle()
      setLifecycle((prev) => (
        prev.length === nextLc.length && prev[prev.length - 1]?.ts === nextLc[nextLc.length - 1]?.ts
          ? prev
          : nextLc
      ))
      const nextConn = readConnLog()
      setConnLog((prev) => (
        prev.length === nextConn.length && prev[prev.length - 1]?.ts === nextConn[nextConn.length - 1]?.ts
          ? prev
          : nextConn
      ))
      // Parked upload dead letters (poisoned CRUD ops the connector discarded).
      const ps = getPowerSync()
      if (ps) {
        try {
          const dls = await ps.getAll<DeadLetterRow>(
            `SELECT id, op_table, op_type, row_id, error_code, discarded_at
               FROM crud_dead_letters ORDER BY discarded_at DESC LIMIT 20`,
          )
          if (!cancelled) {
            setDeadLetters((prev) => (
              prev.length === dls.length && prev[0]?.id === dls[0]?.id ? prev : dls
            ))
          }
        } catch { /* localOnly table absent on stale bundles — show nothing */ }
      }
    }
    void load()
    const id = setInterval(load, 3000)
    return () => { cancelled = true; clearInterval(id) }
  }, [open, refreshTick])

  const intentSummary = useMemo(() => summariseIntents(intents), [intents])

  const ping = useCallback(async (url: string) => {
    const start = performance.now()
    let res: Response | null = null
    let error: string | undefined
    try {
      res = await apiFetch(`${url}?_=${Date.now()}`, {
        method: 'GET',
        cache: 'no-store',
        signal: AbortSignal.timeout(10_000),
      })
    } catch (err) {
      error = String(err)
    }
    const latencyMs = Math.round(performance.now() - start)
    setPings((prev) => [
      {
        url,
        ok: !!res?.ok,
        status: res?.status ?? null,
        latencyMs,
        error,
        at: Date.now(),
      },
      ...prev,
    ].slice(0, 5))
  }, [])

  const dump = useMemo(() => {
    if (!open) return ''
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
    const standalone = typeof window !== 'undefined'
      && (window.matchMedia?.('(display-mode: standalone)')?.matches
        || (window.navigator as Navigator & { standalone?: boolean }).standalone === true)
    const dataFlow = (status.dataFlowStatus ?? {}) as {
      downloading?: boolean
      uploading?: boolean
      downloadError?: { message?: string }
      uploadError?: { message?: string }
    }
    const lines: string[] = [
      `=== imki diagnostics ${new Date().toISOString()} ===`,
      `build: ${buildInfo.buildId}`,
      `userId: ${user?.id ?? 'none'}`,
      `userRole: ${userRole ?? 'none'}`,
      `standalone: ${standalone}`,
      `UA: ${ua}`,
      '',
      '--- PowerSync ---',
      `connected: ${status.connected}`,
      `connecting: ${status.connecting}`,
      `hasSynced: ${status.hasSynced}`,
      `lastSyncedAt: ${status.lastSyncedAt?.toISOString() ?? 'never'}`,
      `downloading: ${!!dataFlow.downloading}`,
      `uploading: ${!!dataFlow.uploading}`,
      `downloadError: ${dataFlow.downloadError?.message ?? 'none'}`,
      `uploadError: ${dataFlow.uploadError?.message ?? 'none'}`,
      '',
      '--- PowerSync lifecycle (newest first) ---',
      ...(lifecycle.length
        ? lifecycle.slice().reverse().map(
          (e) => `${new Date(e.ts).toISOString()} ${e.event}${e.detail ? ` ${e.detail}` : ''}`,
        )
        : ['none']),
      '',
      '--- Connectivity ---',
      `navigator.onLine: ${navOnline}`,
      `ConnectivityService.isOnline: ${svcOnline}`,
      '',
      '--- Connectivity log (newest first) ---',
      ...(connLog.length
        ? connLog.slice().reverse().map(
          (e) => `${new Date(e.ts).toISOString()} ${e.event}${e.detail ? ` ${e.detail}` : ''}`,
        )
        : ['none']),
      '',
      '--- Intent queue ---',
      `total: ${intentSummary.total}`,
      `oldestAge: ${humanAge(intentSummary.oldestAgeMs)}`,
      `byStatus: ${JSON.stringify(intentSummary.byStatus)}`,
      `byType: ${JSON.stringify(intentSummary.byType)}`,
      ...(intentSummary.errorSamples.length
        ? ['errorSamples:', ...intentSummary.errorSamples.map(
          (s) => `  ${s.type} (attempts=${s.attempts}, age=${humanAge(s.ageMs)}): ${s.error}`,
        )]
        : ['errorSamples: none']),
      '',
      '--- Dead letters ---',
      `total: ${deadLetters.length}`,
      ...deadLetters.map((d) => `${d.discarded_at ?? '?'} ${d.op_type} ${d.op_table} ${d.row_id} code=${d.error_code ?? ''}`),
      '',
      '--- Offline blob queue ---',
      `depth: ${offlineQueueDepth ?? '?'}`,
      '',
      '--- Pings ---',
      ...(pings.length
        ? pings.map(
          (p) => `${new Date(p.at).toISOString()} ${p.url} → ${p.ok ? 'OK' : 'FAIL'} status=${p.status} ${p.latencyMs}ms${p.error ? ` err=${p.error}` : ''}`,
        )
        : ['no pings run']),
      '',
      '--- Recent errors ---',
      ...(recentErrors.length
        ? recentErrors.slice().reverse().flatMap((e) => [
          `${new Date(e.ts).toISOString()} [${e.boundary}] ${e.message}${e.digest ? ` (digest=${e.digest})` : ''}`,
          `  url: ${e.url}`,
          ...(e.stack ? ['  stack:', ...e.stack.split('\n').slice(0, 12).map((l) => '    ' + l)] : []),
        ])
        : ['none']),
    ]
    return lines.join('\n')
  }, [open, status, user, userRole, navOnline, svcOnline, intentSummary, offlineQueueDepth, pings, recentErrors, lifecycle, connLog, deadLetters])

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(dump)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Fallback: select text via prompt (iOS-friendly last resort)
      try { window.prompt('Copy diagnostics', dump) } catch { /* noop */ }
    }
  }, [dump])

  return (
    <>
      <div
        // Mobile: stack above the nuke button, clear of the md:hidden tab bar
        // (h-14 + safe-area, z-50). md+: no tab bar, revert to the low spot.
        className="fixed left-3 z-40 bottom-[calc(5.5rem_+_env(safe-area-inset-bottom))] md:bottom-[calc(2.25rem_+_env(safe-area-inset-bottom))]"
        style={{
          paddingLeft: 'env(safe-area-inset-left)',
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Debug diagnostics"
          className={`text-[10px] sm:text-xs rounded-full px-2.5 py-1 backdrop-blur-sm shadow-sm transition-colors font-mono ${
            status.connected && !status.connecting
              ? 'bg-emerald-700/80 hover:bg-emerald-700 text-white'
              : status.connecting
                ? 'bg-amber-600/80 hover:bg-amber-600 text-white'
                : 'bg-rose-700/80 hover:bg-rose-700 text-white'
          }`}
        >
          {open ? 'debug ▼' : `ps=${status.connected ? 'on' : status.connecting ? '…' : 'off'} sync=${status.hasSynced ? 'y' : 'n'}`}
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full sm:max-w-lg max-h-[85vh] bg-white text-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden flex flex-col"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <div className="font-mono text-sm font-semibold">Diagnostics</div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setRefreshTick((t) => t + 1)}
                  className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
                >
                  refresh
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
                >
                  close
                </button>
              </div>
            </div>

            <div className="overflow-auto p-4 text-[11px] sm:text-xs font-mono leading-snug space-y-3">
              {(() => {
                const dataFlow = (status.dataFlowStatus ?? {}) as {
                  downloading?: boolean
                  uploading?: boolean
                  downloadError?: { message?: string }
                  uploadError?: { message?: string }
                }
                return (
                  <Section title="PowerSync">
                    <Row k="connected" v={String(status.connected)} good={status.connected} />
                    <Row k="connecting" v={String(status.connecting)} />
                    <Row k="hasSynced" v={String(status.hasSynced)} good={status.hasSynced} />
                    <Row k="lastSyncedAt" v={status.lastSyncedAt?.toISOString() ?? 'never'} />
                    <Row k="downloading" v={String(!!dataFlow.downloading)} />
                    <Row k="uploading" v={String(!!dataFlow.uploading)} />
                    <Row k="downloadError" v={dataFlow.downloadError?.message ?? 'none'} bad={!!dataFlow.downloadError} />
                    <Row k="uploadError" v={dataFlow.uploadError?.message ?? 'none'} bad={!!dataFlow.uploadError} />
                  </Section>
                )
              })()}

              <Section title={`PowerSync lifecycle (${lifecycle.length})`}>
                {lifecycle.length === 0
                  ? <div className="text-gray-500">none captured</div>
                  : (
                    <>
                      <div className="space-y-0.5">
                        {lifecycle.slice().reverse().map((e, idx) => {
                          const color =
                            e.event === 'connect-rejected' || (e.event === 'status' && e.detail?.startsWith('downloadError'))
                              ? 'text-rose-700'
                              : e.event === 'doc-load'
                                ? 'text-indigo-700 font-semibold'
                              : e.event === 'create' || e.event === 'create-blocked' || e.event === 'teardown-fired'
                                ? 'text-amber-700'
                                : e.event === 'connect-resolved' || (e.event === 'status' && e.detail?.startsWith('connected=true'))
                                  ? 'text-emerald-700'
                                  : 'text-gray-700'
                          return (
                            <div key={idx} className="flex gap-2 break-all">
                              <span className="text-gray-400 shrink-0">{new Date(e.ts).toLocaleTimeString()}</span>
                              <span className={color}>{e.event}{e.detail ? ` ${e.detail}` : ''}</span>
                            </div>
                          )
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={() => { clearPsLifecycle(); setLifecycle([]) }}
                        className="mt-2 text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
                      >
                        clear lifecycle
                      </button>
                    </>
                  )}
              </Section>

              <Section title="Connectivity">
                <Row k="navigator.onLine" v={String(navOnline)} good={navOnline} bad={!navOnline} />
                <Row k="ConnectivityService" v={svcOnline ? 'online' : 'offline'} good={svcOnline} bad={!svcOnline} />
              </Section>

              <Section title={`Intent queue (${intentSummary.total})`}>
                <Row k="oldestAge" v={humanAge(intentSummary.oldestAgeMs)} />
                <Row k="byStatus" v={JSON.stringify(intentSummary.byStatus)} />
                <Row k="byType" v={JSON.stringify(intentSummary.byType)} />
                {intentSummary.errorSamples.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="text-gray-600">recent errors:</div>
                    {intentSummary.errorSamples.map((s, idx) => (
                      <div key={idx} className="pl-2 border-l-2 border-rose-300 text-rose-700 break-all">
                        {s.type} (attempts={s.attempts}, age={humanAge(s.ageMs)}): {s.error}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const n = await reconcilePendingAnalyses()
                        toast.success(`Reconcile: ${n} ${n === 1 ? 'analysis' : 'analyses'} enqueued`)
                        setRefreshTick((t) => t + 1)
                      } catch (err) {
                        toast.error(`Reconcile failed: ${String(err)}`)
                      }
                    }}
                    className="text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    Reconcile
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!confirm('Purge ALL intents from IndexedDB? Any queued uploads/analyses will be dropped.')) return
                      try {
                        const n = await purgeAllIntents()
                        toast.success(`Purged ${n} intent(s)`)
                        setRefreshTick((t) => t + 1)
                      } catch (err) {
                        toast.error(`Purge failed: ${String(err)}`)
                      }
                    }}
                    className="text-xs px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700"
                  >
                    Purge queue
                  </button>
                </div>
              </Section>

              <Section title={`Dead letters (${deadLetters.length})`}>
                {deadLetters.length === 0 && <Row k="status" v="none" good />}
                {deadLetters.map((d) => (
                  <Row
                    key={d.id}
                    k={`${d.op_type} ${d.op_table}`}
                    v={`${d.row_id} code=${d.error_code ?? ''} ${d.discarded_at ?? ''}`}
                    bad
                  />
                ))}
              </Section>

              <Section title="Offline blob queue">
                <Row k="depth" v={offlineQueueDepth == null ? '?' : String(offlineQueueDepth)} />
              </Section>

              <Section title="User">
                <Row k="id" v={user?.id ?? 'none'} />
                <Row k="role" v={userRole ?? 'none'} />
              </Section>

              <Section title={`Recent errors (${recentErrors.length})`}>
                {recentErrors.length === 0
                  ? <div className="text-gray-500">none captured</div>
                  : (
                    <>
                      <div className="space-y-2">
                        {recentErrors.slice().reverse().map((e, idx) => (
                          <div key={idx} className="border-l-2 border-rose-400 pl-2 break-all">
                            <div className="text-rose-700">
                              [{e.boundary}] {e.message}
                              {e.digest ? <span className="text-gray-500"> (digest={e.digest})</span> : null}
                            </div>
                            <div className="text-gray-500">
                              {new Date(e.ts).toLocaleString()} — {e.url}
                            </div>
                            {e.stack && (
                              <details>
                                <summary className="text-gray-600 cursor-pointer">stack</summary>
                                <pre className="text-[10px] whitespace-pre-wrap text-gray-700">{e.stack}</pre>
                              </details>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => { clearErrors(); setRecentErrors([]) }}
                        className="mt-2 text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
                      >
                        clear errors
                      </button>
                    </>
                  )}
              </Section>

              <Section title={`History calls (${historyCalls.length})`}>
                {historyCalls.length === 0
                  ? <div className="text-gray-500">none captured</div>
                  : (
                    <>
                      <div className="text-gray-600 mb-1">
                        peak rate: {Math.max(...historyCalls.map((c) => c.rate))} / 10s
                        {' '}(Safari throws at 100)
                      </div>
                      <div className="space-y-2">
                        {historyCalls.slice().reverse().map((c, idx) => (
                          <div
                            key={idx}
                            className={`border-l-2 pl-2 break-all ${c.rate >= 80 ? 'border-rose-500' : c.rate >= 30 ? 'border-amber-400' : 'border-gray-300'}`}
                          >
                            <div className={c.rate >= 80 ? 'text-rose-700' : 'text-gray-800'}>
                              [{c.kind}] rate={c.rate} → {c.url || '(empty)'}
                            </div>
                            <div className="text-gray-500">
                              {new Date(c.ts).toLocaleTimeString()}
                              {c.sess ? <span className="ml-1">· sess={c.sess}</span> : null}
                            </div>
                            {c.stack && (
                              <details>
                                <summary className="text-gray-600 cursor-pointer">stack</summary>
                                <pre className="text-[10px] whitespace-pre-wrap text-gray-700">{c.stack}</pre>
                              </details>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => { clearHistoryCalls(); setHistoryCalls([]) }}
                        className="mt-2 text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
                      >
                        clear history
                      </button>
                    </>
                  )}
              </Section>

              <Section title="Pings">
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => void ping('/api/health')}
                    className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    GET /api/health
                  </button>
                  <button
                    type="button"
                    onClick={() => void ping('/api/version')}
                    className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    GET /api/version
                  </button>
                </div>
                {pings.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {pings.map((p, idx) => (
                      <div
                        key={idx}
                        className={`break-all ${p.ok ? 'text-emerald-700' : 'text-rose-700'}`}
                      >
                        {new Date(p.at).toLocaleTimeString()} {p.url} → {p.ok ? 'OK' : 'FAIL'}{' '}
                        status={p.status ?? '—'} {p.latencyMs}ms
                        {p.error ? ` err=${p.error}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </div>

            <div className="border-t bg-gray-50 px-4 py-3 flex gap-2">
              <button
                type="button"
                onClick={copy}
                className="text-xs px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-black flex-1"
              >
                {copied ? 'copied ✓' : 'copy all to clipboard'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{title}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  )
}

function Row({ k, v, good, bad }: { k: string; v: string; good?: boolean; bad?: boolean }) {
  const color = bad ? 'text-rose-700' : good ? 'text-emerald-700' : 'text-gray-800'
  return (
    <div className="flex gap-2 break-all">
      <span className="text-gray-500 shrink-0">{k}:</span>
      <span className={color}>{v}</span>
    </div>
  )
}
